// Central error handler for all frontend services
import { getErrorMessage, HTTP_ERROR_MAPPING, ErrorCode, ERROR_MESSAGES } from '@/constants/errorMessages';
import { reportApiError, reportError } from './errorReporting';
import { API_BASE_URL } from '@/config/api'; // Import centralized base URL

// Standard API error response format (matches backend)
export interface ApiErrorResponse {
  success: false;
  errorCode: ErrorCode;
  message: string;
  timestamp: string;
  statusCode: number;
  details?: string;
}

// Standard API success response format
export interface ApiSuccessResponse<T = any> {
  success: true;
  data: T;
  message: string;
  timestamp: string;
  statusCode: number;
}

// Union type for API responses
export type ApiResponse<T = any> = ApiSuccessResponse<T> | ApiErrorResponse;

// Error handling configuration
interface ErrorHandlerConfig {
  logErrors: boolean;
  showUserFriendlyMessages: boolean;
  fallbackMessage: string;
}

const defaultConfig: ErrorHandlerConfig = {
  logErrors: true,
  showUserFriendlyMessages: true,
  fallbackMessage: ERROR_MESSAGES.UNKNOWN_ERROR
};

/**
 * Central error handler class - ALL services must use this
 */
export class ErrorHandler {
  private static config: ErrorHandlerConfig = defaultConfig;

  /**
   * Configure error handler behavior
   */
  static configure(config: Partial<ErrorHandlerConfig>) {
    this.config = { ...this.config, ...config };
  }

  /**
   * Handle fetch response errors with proper error mapping
   * This is the ONLY method services should use for API error handling
   */
  static async handleApiError(response: Response, context?: string): Promise<never> {
    const contextMessage = context ? `[${context}]` : '';
    
    try {
      // Try to parse error response from backend
      const errorData = await response.json();
      
      if (this.config.logErrors) {
        console.error(`${contextMessage} API Error:`, {
          status: response.status,
          statusText: response.statusText,
          url: response.url,
          errorData
        });

      }

      // Check if backend sent structured error response
      if (errorData.errorCode && errorData.message) {
        // Special handling for conflicts with details - normalize phone key but PRESERVE backend message
        if (response.status === 409 && errorData.details) {
          // Normalize phoneNumber -> phone for frontend consistency
          if (errorData.details.phoneNumber && !errorData.details.phone) {
            errorData.details.phone = errorData.details.phoneNumber;
          }
          
          // Set more specific errorCode for UI logic but DON'T override message
          if (errorData.details.email && errorData.details.phone) {
            // Both conflicts - keep VALIDATION_ERROR
          } else if (errorData.details.email) {
            errorData.errorCode = ErrorCode.EMAIL_ALREADY_EXISTS;
          } else if (errorData.details.phone) {
            errorData.errorCode = ErrorCode.PHONE_ALREADY_REGISTERED;
          }
        }
        // Backend sent proper structured error - ALWAYS use backend message first
        const userMessage = errorData.message || ERROR_MESSAGES[errorData.errorCode as ErrorCode] || 'Something went wrong. Please try again.';
        const apiError = new ApiError(errorData.errorCode, userMessage, response.status, errorData.details);
        
        // Report to error tracking
        reportApiError(apiError, response.url, response.status);
        
        throw apiError;
      }

      // Fallback to HTTP status code mapping
      const statusCode = response.status as keyof typeof HTTP_ERROR_MAPPING;
      const friendlyMessage = HTTP_ERROR_MAPPING[statusCode];
      
      if (friendlyMessage) {
        const apiError = new ApiError(this.mapStatusToErrorCode(response.status), friendlyMessage, response.status);
        reportApiError(apiError, response.url, response.status);
        throw apiError;
      }

      // Last resort - use backend message or generic error
      const errorMessage = errorData.message || errorData.error || `HTTP ${response.status}: ${response.statusText}`;
      const userFriendlyMessage = this.config.showUserFriendlyMessages 
        ? getErrorMessage(errorMessage) 
        : errorMessage;
      
      throw new ApiError(ErrorCode.UNKNOWN_ERROR, userFriendlyMessage, response.status);

    } catch (parseError) {
      // If it's already an ApiError, re-throw it (don't treat as parse error)
      if (parseError instanceof ApiError) {
        throw parseError;
      }
      
      // Response is not JSON - use HTTP status mapping
      if (this.config.logErrors) {
        console.error(`${contextMessage} Non-JSON Error Response:`, {
          status: response.status,
          statusText: response.statusText,
          url: response.url,
          parseError
        });
      }

      const statusCode = response.status as keyof typeof HTTP_ERROR_MAPPING;
      const friendlyMessage = HTTP_ERROR_MAPPING[statusCode] || `HTTP ${response.status}: ${response.statusText}`;
      
      throw new ApiError(this.mapStatusToErrorCode(response.status), friendlyMessage, response.status);
    }
  }

  /**
   * Handle generic errors (network, parsing, etc.)
   */
  static handleGenericError(error: any, context?: string): never {
    const contextMessage = context ? `[${context}]` : '';
    
    if (this.config.logErrors) {
      console.error(`${contextMessage} Generic Error:`, error);
    }

    // If it's already an ApiError, re-throw it
    if (error instanceof ApiError) {
      throw error;
    }

    // Handle network errors
    if (error instanceof TypeError && error.message.includes('fetch')) {
      throw new ApiError(ErrorCode.NETWORK_ERROR, ERROR_MESSAGES.NETWORK_ERROR);
    }

    // Handle timeout errors
    if (error.name === 'AbortError' || error.message.includes('timeout')) {
      throw new ApiError(ErrorCode.REQUEST_TIMEOUT, ERROR_MESSAGES.REQUEST_TIMEOUT);
    }

    // Handle other Error objects
    if (error instanceof Error) {
      const userFriendlyMessage = this.config.showUserFriendlyMessages 
        ? getErrorMessage(error.message) 
        : error.message;
      throw new ApiError(ErrorCode.UNKNOWN_ERROR, userFriendlyMessage);
    }

    // Fallback for unknown error types
    throw new ApiError(ErrorCode.UNKNOWN_ERROR, this.config.fallbackMessage);
  }

  /**
   * Map HTTP status codes to error codes
   */
  private static mapStatusToErrorCode(status: number): ErrorCode {
    switch (status) {
      case 400:
        return ErrorCode.INVALID_FORMAT;
      case 401:
        return ErrorCode.INVALID_OTP;
      case 403:
        return ErrorCode.PHONE_ALREADY_REGISTERED;
      case 404:
        return ErrorCode.NETWORK_ERROR;
      case 408:
        return ErrorCode.REQUEST_TIMEOUT;
      case 429:
        return ErrorCode.TOO_MANY_REQUESTS;
      case 500:
      case 502:
      case 503:
        return ErrorCode.SERVER_ERROR;
      case 504:
        return ErrorCode.REQUEST_TIMEOUT;
      default:
        return ErrorCode.UNKNOWN_ERROR;
    }
  }

  /**
   * Create a standardized fetch wrapper that all services MUST use
   */
  static async safeFetch(
    url: string, 
    options: RequestInit = {}, 
    context?: string
  ): Promise<Response> {
    try {
      const response = await fetch(url, options);
      
      if (!response.ok) {
        await this.handleApiError(response, context);
      }

      return response;
    } catch (error) {
      // If it's already an ApiError from handleApiError, re-throw
      if (error instanceof ApiError) {
        throw error;
      }
      
      // Handle other errors (network, etc.)
      this.handleGenericError(error, context);
    }
  }

  /**
   * Parse JSON response with error handling
   */
  static async parseJsonResponse<T>(response: Response, context?: string): Promise<T> {
    try {
      return await response.json();
    } catch (error) {
      this.handleGenericError(new Error('Failed to parse response'), context);
    }
  }
}

/**
 * Custom error class for API errors
 */
export class ApiError extends Error {
  constructor(
    public readonly errorCode: ErrorCode,
    public readonly userMessage: string,
    public readonly statusCode?: number,
    public readonly details?: any
  ) {
    super(userMessage);
    this.name = 'ApiError';
  }

  /**
   * Get user-friendly error message
   */
  getUserMessage(): string {
    return this.userMessage;
  }

  /**
   * Check if error is of specific type
   */
  isErrorCode(code: ErrorCode): boolean {
    return this.errorCode === code;
  }

  getDetails<T=any>(): T | undefined {
    return this.details as T;
  }
}

/**
 * Service base class that enforces centralized error handling
 */
export abstract class BaseService {
  protected baseUrl: string;
  protected serviceName: string;

  constructor(serviceName: string, baseUrl?: string) {
    this.serviceName = serviceName;
    // Use centralized API_BASE_URL from config
    this.baseUrl = baseUrl || API_BASE_URL;
  }

  /**
   * Make authenticated API call - all services should use this
   * Enhanced with backend-compatible headers and JWT handling
   */
  protected async apiCall<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const url = `${this.baseUrl}${endpoint}`;
    const context = `${this.serviceName}:${endpoint}`;

    // Get auth token and user info
    const token = this.getAuthToken();
    const currentRole = this.getCurrentRole();
    
    // Build headers compatible with backend expectations
    const headers = {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
      // Backend expects these headers (from JWT strategy)
      'x-platform': 'fabrics', // This app is fabric buyer only
      'x-app-name': 'fabric-buyer-app',
      ...(token && { 'Authorization': `Bearer ${token}` }),
      ...(currentRole && { 'x-current-role': currentRole }),
      ...options.headers
    };

    // Add timeout from environment config
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), parseInt(import.meta.env.VITE_API_TIMEOUT || '30000', 10));

    try {
      const response = await ErrorHandler.safeFetch(url, {
        ...options,
        headers,
        signal: controller.signal
      }, context);

      clearTimeout(timeoutId);
      return ErrorHandler.parseJsonResponse<T>(response, context);
    } catch (error) {
      clearTimeout(timeoutId);
      throw error;
    }
  }

  /**
   * Get auth token from localStorage (Vite apps use localStorage by default)
   */
  protected getAuthToken(): string | null {
    return localStorage.getItem('authToken') || localStorage.getItem('access_token');
  }

  /**
   * Get current user role (for multi-role support like backend)
   */
  protected getCurrentRole(): string | null {
    return localStorage.getItem('currentRole') || localStorage.getItem('user_role');
  }

  /**
   * Set auth token in localStorage
   */
  protected setAuthToken(token: string): void {
    localStorage.setItem('authToken', token);
  }

  /**
   * Set current role in localStorage
   */
  protected setCurrentRole(role: string): void {
    localStorage.setItem('currentRole', role);
  }

  /**
   * Clear all auth data
   */
  protected clearAuthData(): void {
    localStorage.removeItem('authToken');
    localStorage.removeItem('access_token');
    localStorage.removeItem('currentRole');
    localStorage.removeItem('user_role');
    localStorage.removeItem('refresh_token');
  }
}

// Export error handling utilities
export { getErrorMessage, ERROR_MESSAGES, ErrorCode } from '@/constants/errorMessages'; 